import { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  createProject as createProjectOnServer,
  deleteProject as deleteProjectOnServer,
  listProjects as listProjectsFromServer,
  moveChatToProject as moveChatToProjectOnServer,
  updateProject as updateProjectOnServer,
} from '../../utils/projectsApi';

const ACTIVE_PROJECT_STORAGE_KEY = 'arrowai-active-project-id';

const getMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortProjects = (projects = []) =>
  [...projects]
    .filter((project) => !project.archived)
    .sort((a, b) => getMillis(b.updatedAt) - getMillis(a.updatedAt));

export const getChatProjectId = (chat = {}) =>
  chat?.projectId || chat?.project_id || null;

export const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(() =>
    localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY) || null
  );
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectError, setProjectError] = useState('');

  const reconcileActiveProject = useCallback((nextProjects = []) => {
    const storedActiveProjectId = localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY);
    if (
      storedActiveProjectId &&
      !nextProjects.some((project) => project.id === storedActiveProjectId)
    ) {
      setActiveProjectId(null);
      localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    if (!auth.currentUser) {
      setProjects([]);
      setIsLoadingProjects(false);
      return [];
    }

    setIsLoadingProjects(true);
    setProjectError('');
    try {
      const payload = await listProjectsFromServer();
      const nextProjects = sortProjects(payload.projects || []);
      setProjects(nextProjects);
      reconcileActiveProject(nextProjects);
      return nextProjects;
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      setProjectError(error.message || 'Failed to load workspaces');
      return [];
    } finally {
      setIsLoadingProjects(false);
    }
  }, [reconcileActiveProject]);

  useEffect(() => {
    let isMounted = true;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;

      if (!user) {
        setProjects([]);
        setActiveProjectId(null);
        setProjectError('');
        setIsLoadingProjects(false);
        localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
        return;
      }

      await refreshProjects();
    });

    return () => {
      isMounted = false;
      unsubscribeAuth();
    };
  }, [refreshProjects]);

  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeProjectId) || null,
    [activeProjectId, projects]
  );

  const selectProject = useCallback((projectId) => {
    const nextProjectId = projectId || null;
    setActiveProjectId(nextProjectId);
    if (nextProjectId) {
      localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, nextProjectId);
    } else {
      localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
    }
  }, []);

  const createProject = useCallback(async (payload) => {
    const result = await createProjectOnServer(payload);
    if (result?.project?.id) {
      setProjects((prev) => sortProjects([result.project, ...prev]));
      selectProject(result.project.id);
    } else {
      await refreshProjects();
    }
    return result;
  }, [refreshProjects, selectProject]);

  const updateProject = useCallback(async (payload) => {
    const result = await updateProjectOnServer(payload);
    if (result?.project?.id) {
      setProjects((prev) =>
        sortProjects(prev.map((project) =>
          project.id === result.project.id ? result.project : project
        ))
      );
    } else {
      await refreshProjects();
    }
    return result;
  }, [refreshProjects]);

  const deleteProject = useCallback(async (projectId) => {
    const result = await deleteProjectOnServer(projectId);
    setProjects((prev) => prev.filter((project) => project.id !== projectId));
    if (activeProjectId === projectId) {
      selectProject(null);
    }
    return result;
  }, [activeProjectId, selectProject]);

  const moveChatToProject = useCallback((payload) => moveChatToProjectOnServer(payload), []);

  return {
    activeProject,
    activeProjectId,
    createProject,
    deleteProject,
    isLoadingProjects,
    moveChatToProject,
    projectError,
    projects,
    refreshProjects,
    selectProject,
    updateProject,
  };
};
